import React from "react";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import { Fab, Tooltip } from "@thingsw/pitta-design-system";
import { useMediaQuery } from "@material-ui/core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import GpsFixedIcon from "@material-ui/icons/GpsFixed";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";

import ReactLoading from "react-loading";

import { useDispatch, useSelector } from "react-redux";
import { setLoadingLocation, THEME } from "../../features/Theme/slice";
import { RootState } from "../../features/store";
import { Webviewer, LightColors } from "@thingsw/pitta-modules";

const useStyles = makeStyles((theme: Theme) => ({
  zoomControlDiv: {
    position: "absolute",
    bottom: 0,
    ...(theme.direction === "rtl" ? { left: 0 } : { right: 0 }),
    padding: theme.spacing(2, 1, 4, 2),
    display: "flex",
    overflow: "visible",
    flexDirection: "column",

    [theme.breakpoints.up(Webviewer.mobile)]: {
      paddingBottom: theme.spacing(2),
    },
    zIndex: 80,
  },
  zoomControlEventMapDiv: {
    padding: theme.spacing(2, 1.875, 4, 2),
    [theme.breakpoints.up(Webviewer.mobile)]: {
      paddingBottom: theme.spacing(4) - 1,
    },
  },
  zoomControlBtn: {
    borderRadius: 8,
    width: 30,
    height: 30,
    color: LightColors.primary["3"],
    minHeight: 30,
  },
  eventMapControlBtn: {
    height: "29px !important",
    minHeight: "29px !important",
  },
  gpsFixBtn: {
    marginBottom: theme.spacing(0.5),
  },
  zoomInBtn: {
    borderBottom: 0,
    borderRadius: theme.spacing(1, 1, 0, 0),
    boxShadow: "none",
  },
  zoomOutBtn: {
    borderTop: 0,
    borderRadius: theme.spacing(0, 0, 1, 1),
    boxShadow: "none",
  },
  noHover: {
    "&:hover": {
      fill: LightColors.primary["3"],
      color: LightColors.primary["3"],
    },
    "&:active": {
      fill: LightColors.primary["7"],
      color: LightColors.primary["7"],
    },
  },
  noFocus: {
    "&:focus": {
      fill: LightColors.primary["3"],
      color: LightColors.primary["3"],
    },
  },
}));

interface ZoomPanelProps {
  onMyLocation: (position: GeolocationPosition) => void;
  onFailedLocation?: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  locPerm?: number;
  eventMap?: boolean;
}

export const ZoomPanel = ({
  onMyLocation,
  onFailedLocation,
  onZoomIn,
  onZoomOut,
  locPerm,
  eventMap,
}: ZoomPanelProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme() as Theme;
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  const { loadingLocation } = useSelector((state: RootState) => state[THEME]);

  return (
    <div
      className={clsx(
        classes.zoomControlDiv,
        eventMap && classes.zoomControlEventMapDiv
      )}
    >
      <Tooltip
        disableTouchListener={mobile}
        title={t("My location") ?? "My location"}
        PopperProps={{
          disablePortal: true,
          modifiers: {
            preventOverflow: { enabled: false },
            flip: { enabled: false },
          },
        }}
        placement={theme.direction === "rtl" ? "right" : "left"}
        className={clsx(classes.noFocus, { [classes.noHover]: mobile })}
      >
        <Fab
          size="small"
          variant="rounded"
          className={clsx(classes.zoomControlBtn, classes.gpsFixBtn, {
            [classes.eventMapControlBtn]: eventMap,
          })}
          onClick={() => {
            if (locPerm === 0) {
              return onFailedLocation?.();
            }
            if (navigator.geolocation) {
              dispatch(setLoadingLocation(true));

              const timer = setTimeout(() => {
                dispatch(setLoadingLocation(false));
              }, 5500);
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  clearTimeout(timer);
                  onMyLocation(position);
                },
                (e) => {
                  console.log("getCurrentPosition is failed", e);
                  dispatch(setLoadingLocation(false));
                  if (e.code === e.PERMISSION_DENIED) {
                    onFailedLocation?.();
                  }
                },
                { timeout: 10000 }
              );
            }
          }}
        >
          {loadingLocation ? (
            <ReactLoading
              type="spokes"
              color={LightColors.primary["4"]}
              height={18}
              width={18}
            />
          ) : (
            <GpsFixedIcon fontSize="small" />
          )}
        </Fab>
      </Tooltip>
      {!mobile && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            borderRadius: 8,
            boxShadow:
              "0px 0px 1px rgba(0, 0, 0, 0.14), 0px 1px 1px rgba(0, 0, 0, 0.12), 0px 0px 3px rgba(0, 0, 0, 0.2)",
          }}
        >
          <Tooltip
            title={t("Zoom in") ?? "Zoom in"}
            PopperProps={{
              disablePortal: true,
              modifiers: {
                preventOverflow: { enabled: false },
                flip: { enabled: false },
              },
            }}
            placement={theme.direction === "rtl" ? "right" : "left"}
          >
            <Fab
              size="small"
              variant="rounded"
              className={clsx(classes.zoomControlBtn, classes.zoomInBtn, {
                [classes.eventMapControlBtn]: eventMap,
              })}
              onClick={onZoomIn}
            >
              <AddIcon fontSize="small" />
            </Fab>
          </Tooltip>
          <Tooltip
            title={t("Zoom out") ?? "Zoom out"}
            PopperProps={{
              disablePortal: true,
              modifiers: {
                preventOverflow: { enabled: false },
                flip: { enabled: false },
              },
            }}
            placement={theme.direction === "rtl" ? "right" : "left"}
          >
            <Fab
              size="small"
              variant="rounded"
              className={clsx(classes.zoomControlBtn, classes.zoomOutBtn, {
                [classes.eventMapControlBtn]: eventMap,
              })}
              onClick={onZoomOut}
            >
              <RemoveIcon fontSize="small" />
            </Fab>
          </Tooltip>
        </div>
      )}
    </div>
  );
};
